import React, { useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import { useFamily } from '../../context/FamilyContext';
import { Home, Users, User, LogOut, Settings, Menu, X, Heart, Lightbulb } from 'lucide-react';
import piggyLogo from '../../assets/Logo/Piggy2.png';
import { fetchVersionNumber } from '../../api';
import ModalGrowCommunity from './ModalGrowCommunity';
import Confetti from 'react-confetti';

import './MySidebarFamily.css';

const useDynamicHeight = () => {
    const ref = useRef(null);
  
    const updateHeight = useCallback(() => {
      if (ref.current) {
        ref.current.style.height = `${window.innerHeight}px`;
      }
    }, []);
  
    useEffect(() => {
      updateHeight();
      window.addEventListener('resize', updateHeight);
      return () => window.removeEventListener('resize', updateHeight);
    }, [updateHeight]);
  
    return ref;
  };


const MySidebarFamily = () => {
    const { logout } = useContext(UserContext);
    const navigate = useNavigate();
    const location = useLocation();
    const { familyChildren } = useFamily();
    const [version, setVersion] = useState('');

    const checkIsMobile = useCallback(() => window.innerWidth <= 768, []);
    const [isMobile, setIsMobile] = useState(checkIsMobile());
    const [isCollapsed, setIsCollapsed] = useState(isMobile);

    const [isGrowCommunityModalOpen, setIsGrowCommunityModalOpen] = useState(false);
    const sidebarRef = useDynamicHeight();



    useEffect(() => {
        fetchVersionNumber()
            .then(versionNumber => setVersion(versionNumber))
            .catch(error => console.error("Failed to fetch version number:", error));
    }, []);

    useEffect(() => {
        const handleResize = () => {
            const newIsMobile = checkIsMobile();
            setIsMobile(newIsMobile);
            if (newIsMobile) {
                setIsCollapsed(true);
            } else if (isMobile) {
                setIsCollapsed(false);
            }
        };
        
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [checkIsMobile, isMobile]);

    const handleLogout = useCallback(() => {
        logout();
        navigate('/auth/login');
    }, [logout, navigate]);

    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
    };

    const handleNavigation = useCallback((to) => {
        navigate(to);
        if (isMobile) {
            setIsCollapsed(true);
        }
    }, [isMobile, navigate]);

    const handleSuggestReport = () => {
        window.open("https://community.piggybunker.com", "_blank", "noopener,noreferrer");
        console.log("Suggest & Report clicked");
        if (isMobile) {
            setIsCollapsed(true);
        }
    };

    const handleGrowCommunity = () => {
        setIsGrowCommunityModalOpen(true);
        console.log("Grow Our Community clicked");
        if (isMobile) {
            setIsCollapsed(true);
        }
    };

    return (

        <div 
        ref={sidebarRef}
        className={`sidebar-container ${isCollapsed ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''}`}
        >
            <div className={`sidebar-content ${isCollapsed ? 'collapsed' : ''}`}>
                <div className="sidebar-top-section">
                    <div className="sidebar-logo">
                        <img src={piggyLogo} alt="Logo" className="sidebar-logo-image" />
                        <h1 className="sidebar-product-name">Piggy Bunker</h1>
                    </div>
                    <nav className="sidebar-navigation">
                        <NavItem to="/family/dashboard" icon={<Home size={24} />} label="Dashboard" onClick={() => handleNavigation('/family/dashboard')} />
                        <NavItem to="/family/manage" icon={<Users size={24} />} label="Manage Family" onClick={() => handleNavigation('/family/manage')} />
                        
                        <div className="children-section">
                            <h3 className="children-header">Children</h3>
                            {familyChildren.map(child => (
                                <NavItem 
                                    key={child.childId}
                                    to={`/family/${child.name}`}
                                    icon={<User size={24} />}
                                    label={child.display_name}
                                    onClick={() => handleNavigation(`/family/${child.name}`)}
                                />
                            ))}
                        </div>
                    </nav>
                </div>
                <div className="sidebar-bottom-section">
                    <div className="sidebar-nav-item suggest-report" onClick={handleSuggestReport}>
                        <Lightbulb size={24} className="light-bulb" strokeWidth={2} />
                        <span className="sidebar-nav-text">Suggest & Report</span>
                    </div>
                    <div className="sidebar-nav-item grow-community" onClick={handleGrowCommunity}>
                        <Heart size={24} className="green-heart" strokeWidth={2} />
                        <span className="sidebar-nav-text">Grow Our Community</span>
                    </div>
                    <div className="sidebar-spacing"></div>
                    <NavItem to="/family/settings" icon={<Settings size={24} />} label="Settings" onClick={() => handleNavigation('/family/settings')} />
                    <div className="sidebar-nav-item" onClick={handleLogout}>
                        <LogOut size={24} />
                        <span className="sidebar-nav-text">Logout</span>
                    </div>
                    <p className="sidebar-version">Version: {version}</p>
                    <p className="sidebar-version">GenericApp Initiative</p>
                </div>
            </div>
            <button 
                className={`sidebar-toggle-button ${isCollapsed ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''}`} 
                onClick={toggleSidebar}
            >
                {isCollapsed ? <Menu size={24} /> : (isMobile ? <X size={24} /> : null)}
            </button>
            <ModalGrowCommunity
                isOpen={isGrowCommunityModalOpen}
                onClose={() => setIsGrowCommunityModalOpen(false)}
            />

        </div>
    );
};

const NavItem = ({ to, icon, label, onClick }) => {
    const location = useLocation();
    const isSelected = location.pathname === to;

    return (
        <div 
            className={`sidebar-nav-item ${isSelected ? 'selected' : ''}`}
            onClick={onClick}
        >
            {icon}
            <span className="sidebar-nav-text">{label}</span>
        </div>
    );
};

export default MySidebarFamily;